/**
 * AsaasSeal — o selo "Serviços financeiros Asaas" nas telas onde há dinheiro.
 *
 * Exigência regulatória, não enfeite: ver o cabeçalho de
 * `src/lib/asaasDisclosure.ts`. Este componente existe para que a regra de COMO
 * aplicar o selo (URL remota, tamanho, link, referrer) more num lugar só — cada
 * tela decide ONDE ele aparece, nunca COMO.
 *
 * Três decisões que quebram a conformidade se alguém "simplificar":
 *
 * 1. **O `<img>` NÃO leva `referrerPolicy="no-referrer"`.** O Asaas confere pelo
 *    `Referer` se a aplicação carregou o selo — sem ele, do lado deles a
 *    integração parece não ter selo nenhum. O `rel="noopener noreferrer"` do
 *    `<a>` é outra coisa (segurança do `target="_blank"`, e o playbook o pede
 *    explicitamente): ele governa a NAVEGAÇÃO, não o carregamento da imagem.
 * 2. **A imagem vem da CDN do Asaas, não do bundle.** O arquivo é atualizado por
 *    eles de forma centralizada; embarcá-lo congela a marca numa versão antiga.
 * 3. **Offline cai para a menção TEXTUAL.** O app é offline-first e a CDN não
 *    está no cache — sem fallback, a tela ficaria com um buraco justamente onde
 *    a norma exige identificação. O texto cumpre a mesma exigência, e é por isso
 *    que ele é o `onError` e não um espaço vazio.
 *
 * O par claro/escuro é renderizado junto e alternado por CSS: o app tem tema
 * escuro, e o selo positivo (traço escuro) some sobre o card escuro. As duas
 * imagens carregam, o que é indiferente para o Asaas — ambas as URLs são do
 * mesmo `id`.
 */
import { useState } from 'react';
import {
  ASAAS_SEAL_ALT,
  ASAAS_SEAL_SIZE,
  ASAAS_SEAL_URLS,
  ASAAS_SHORT_DISCLOSURE,
  ASAAS_SITE_URL,
} from '@/lib/asaasDisclosure';

interface Props {
  /** Classe do wrapper — use para espaçar o selo no bloco que o hospeda. */
  className?: string;
}

export function AsaasSeal({ className }: Props) {
  const [failed, setFailed] = useState(false);

  // O selo é CENTRALIZADO em todas as superfícies, e por isso o alinhamento
  // mora aqui e não em cada tela: são cinco pontos de contato hoje, e o
  // playbook cobra que a aplicação da marca seja consistente. Centrado por
  // fora, um sexto ponto nasceria desalinhado sem ninguém notar — a mesma
  // razão de as URLs e os textos morarem em `asaasDisclosure.ts`.
  const wrapper = `flex justify-center ${className ?? ''}`;

  if (failed) {
    return (
      <div className={wrapper}>
        <p className="text-center text-[11px] leading-snug text-muted-foreground">
          {ASAAS_SHORT_DISCLOSURE}
        </p>
      </div>
    );
  }

  return (
    <div className={wrapper}>
      <a
        href={ASAAS_SITE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-block"
        aria-label={`${ASAAS_SEAL_ALT} — abrir o site do Asaas`}
      >
        <img
          src={ASAAS_SEAL_URLS.positivo}
          alt={ASAAS_SEAL_ALT}
          width={ASAAS_SEAL_SIZE.width}
          height={ASAAS_SEAL_SIZE.height}
          onError={() => setFailed(true)}
          className="inline-block max-w-full dark:hidden"
        />
        <img
          src={ASAAS_SEAL_URLS.negativoBranco}
          alt={ASAAS_SEAL_ALT}
          width={ASAAS_SEAL_SIZE.width}
          height={ASAAS_SEAL_SIZE.height}
          onError={() => setFailed(true)}
          className="hidden max-w-full dark:inline-block"
        />
      </a>
    </div>
  );
}
